import { Button } from "components/Button";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { dAppsAPI } from "screens/DApps/service";
import { useDAppsStore } from "screens/DApps/store";
import { DAppCard } from "../DAppCard";
import { ProjectAddForm } from "./components/ProjectAddForm";
import { ProjectEditForm } from "./components/ProjectEditForm";
import { SquareLoader } from "screens/Main/components/Loader/SquareLoader";
import { DeleteModal } from "../DeleteModal";
import clsx from "clsx";

export const DAppsPannel = () => {
  const {
    dApps,
    page,
    hasMore,
    dAppForEdit,
    setDApps,
    addDApps,
    setPage,
    setHasMore,
    setDAppForEdit,
  } = useDAppsStore();
  const [isAddFormOpen, setIsAddFormOpen] = useState(false);
  const [totalProjectsItems, setTotalProjectsIstems] = useState(0);
  const [totalPages, setTotalpages] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadMoreLoading, setIsLoadMoreLoading] = useState(false);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [idToDelete, setIdToDelete] = useState<string>("");

  useEffect(() => {
    setIsLoading(true);
    dAppsAPI.getTags();
    dAppsAPI.getEcosystems();
    const loadInitialData = async () => {
      try {
        const { data, meta } = await dAppsAPI.getDApps(page);
        if (data.length === 0) {
          setHasMore(false);
        }
        setTotalProjectsIstems(meta.totalItems);

        setTotalpages(meta.totalPages);
        const isThereMore = meta.totalPages !== page;
        setHasMore(isThereMore);
        setPage(page);
        setDApps(data);
      } catch {
        console.error("Error fetching data");
      } finally {
        setIsLoading(false);
      }
    };
    loadInitialData();
    // eslint-disable-next-line
  }, []);

  const loadMore = async () => {
    if (!hasMore) return;
    if (totalPages < page + 1) return;

    setIsLoadMoreLoading(true);

    const nextPage = page + 1;

    try {
      const { data, meta } = await dAppsAPI.getDApps(nextPage);
      addDApps(data);

      const isThereMore = nextPage < meta.totalPages;

      setHasMore(isThereMore);
      setPage(isThereMore ? nextPage : page);
    } catch (error) {
      console.error("Error loading data", error);
    } finally {
      setIsLoadMoreLoading(false);
    }
  };

  useEffect(() => {
    if (isAddFormOpen || !!dAppForEdit) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isAddFormOpen, dAppForEdit]);

  const onDelete = () => {
    if (idToDelete) {
      dAppsAPI.deleteDApp({ dAppId: idToDelete });
      setIsDeleteModalOpen(false);
    }
  };

  return (
    <div className="px-[14px] sm:px-[154px] flex flex-col items-center">
      <h1 className="mb-[50px] text-[24px] text-center px-[50px] sm:px-0 md:text-[36px] font-[700]">
        dApps Admin Panel
      </h1>

      <Button
        extraClass="flex items-center justify-center mb-[60px] min-w-[192px]"
        onClick={() => setIsAddFormOpen(true)}
      >
        <span className="mr-[10px] text-[18px]">+</span> Add new project
      </Button>

      <div className="w-full">
        <p className="font-[700] mb-[24px]">
          All projects ({totalProjectsItems})
        </p>
        {isLoading ? (
          <div className="flex w-full  my-[20px] justify-center  items-center">
            <SquareLoader size={32} />
          </div>
        ) : (
          <InfiniteScroll
            dataLength={dApps.length}
            next={() => loadMore()}
            hasMore={hasMore}
            loader={<p></p>}
            endMessage={<p></p>}
          >
            <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-7 w-full mb-16">
              {dApps.length > 0 ? (
                dApps?.map((dApp, i) => (
                  <motion.article
                    initial={{
                      y: 50,
                      opacity: 0,
                    }}
                    whileInView={{
                      y: 0,
                      opacity: 1,
                    }}
                    transition={{
                      duration: 0.1,
                      delay: 0.1 * (i % 3),
                    }}
                    viewport={{
                      once: true,
                      margin: "-20% 0%",
                    }}
                    key={dApp.id || `${dApp.name}-${i}`}
                    className="relative cursor-pointer flex flex-col gap-[8px] min-h-[251px] py-[22px] pl-[19px] pr-[24px] bg-[#171717]"
                    onClick={() => window.open(dApp.website, "_blank")}
                  >
                    <DAppCard
                      setIsDeleteModalOpen={setIsDeleteModalOpen}
                      setIdToDelete={setIdToDelete}
                      dApp={dApp}
                    />
                  </motion.article>
                ))
              ) : (
                <p className="W-full flex items-center justify-center mt-[50px]">
                  No projects available
                </p>
              )}
            </section>
          </InfiniteScroll>
        )}
        {hasMore && !isLoading && (
          <div className="flex w-full justify-center">
            <Button
              extraClass="flex items-center justify-center mb-[60px] min-w-[192px]"
              onClick={loadMore}
            >
              {isLoadMoreLoading ? (
                <div className="flex w-full justify-center  items-center">
                  <SquareLoader size={32} />
                </div>
              ) : (
                <span> Load more...</span>
              )}
            </Button>
          </div>
        )}
      </div>

      <ProjectAddForm
        isOpen={isAddFormOpen}
        onClose={() => setIsAddFormOpen(false)}
      />

      <div
        className={clsx(
          "fixed inset-0  items-center justify-center z-50",
          isDeleteModalOpen ? "flex" : "hidden"
        )}
      >
        <DeleteModal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onDelete={onDelete}
        />
      </div>

      {!!dAppForEdit && (
        <ProjectEditForm
          dApp={dAppForEdit}
          isOpen={!!dAppForEdit}
          onClose={() => setDAppForEdit(null)}
        />
      )}
    </div>
  );
};
